<template>
    <oss-dialog
        v-model="dialog"
        title="Document à importer"
        @valid="uploadDocumentsFromMail()"
        :previousDisplay="false"
        :validLoading="loading"
        :validTitle="actionTitle"
        :validDisabled="actionDisabled"
        min-width="800px"
    >
        <v-row no-gutters class="d-flex align-center">
            <v-col cols="6">
                <oss-date-picker-filled
                    v-model="startDate"
                    :hide-details="true"
                    label="Date de début"
                    :mandatory="false"
                    :textFieldReadOnly="false"
                />
            </v-col>
            <v-col cols="6" class="pl-2">
                <oss-date-picker-filled
                    v-model="endDate"
                    :hide-details="true"
                    label="Date de fin"
                    :mandatory="false"
                    readonly
                    :textFieldReadOnly="false"
                />
            </v-col>
        </v-row>
        <v-row no-gutters class="d-flex align-center">
            <v-col cols="12" class="mt-2">
                <v-switch v-model="onlyToUpload" :label="'A importer uniquement'" />
            </v-col>
        </v-row>
        <v-row no-gutters class="d-flex align-center" v-if="report">
            {{ report }}
        </v-row>
        <v-list style="background-color: transparent" lines="two">
            <v-virtual-scroll
                :item-height="75"
                :items="filteredAttachments"
                height="400"
                class="py-3"
                bench="2"
            >
                <template v-slot="{ item }">
                    <v-list-item
                        :key="item.importId"
                        class="oss-list-item-inactive"
                        :active="false"
                        :link="false"
                    >
                        <v-row no-gutters>
                            <!-- <v-col cols="1" class="d-flex align-center">
                                    <v-checkbox
                                        color="primary"
                                        :value="item.selected"
                                        @update:model-value="item.selected = !item.selected"
                                        :ripple="false"
                                        v-if="!item.document"
                                    ></v-checkbox>
                                </v-col> -->
                            <v-col cols="8">
                                <v-row no-gutters class="d-flex align-center">
                                    <v-col
                                        cols="12"
                                        class="d-flex pb-2 font-weight-bold align-center"
                                    >
                                        <v-icon left color="secondary">$label</v-icon>
                                    </v-col>
                                    <v-col cols="12" class="px-1 d-flex align-center">
                                        <v-icon color="secondary" size="small" location="start"
                                            >$oss-calendar</v-icon
                                        >{{ formatDate(item.message.date) }}
                                        {{ getAttachmentType(item) }} - {{ getTo(item) }}
                                    </v-col>
                                </v-row>
                            </v-col>
                            <v-col cols="4">
                                <v-row no-gutters class="d-flex">
                                    <v-col cols="12" class="d-flex font-weight-bold">
                                        {{ getAttachmentStatus(item) }}
                                    </v-col>
                                    <v-col cols="12" class="d-flex caption">
                                        {{ "De: " + getSenderOrReceiver(item.from) }}
                                    </v-col>
                                    <v-col cols="12" class="d-flex pb-2 caption">
                                        {{ "&#xc0; : " + getSenderOrReceiver(item.to) }}
                                    </v-col>
                                </v-row>
                            </v-col>
                        </v-row>
                    </v-list-item>
                </template>
            </v-virtual-scroll>
        </v-list>
    </oss-dialog>
</template>

<script lang="ts">
import moment from "moment-timezone";
import { findEmailIntoString } from "../../services/emailServices";
import UPLOAD_MAIL_DOCUMENTS from "../../graphql/document/UploadMailDocuments.gql";
import { Component, Vue, toNative } from "vue-facing-decorator";
import { Model } from "vue-facing-decorator";
import { Watch } from "vue-facing-decorator";

@Component({
    emits: ["attachment-uploaded"],
})
export class UploadDocumentsFromMail extends Vue {
    @Model({ type: Boolean }) value: Boolean;

    get dialog() {
        return this.value;
    }

    set dialog(event) {
        this.value = event;
    }

    loading: boolean = false;

    attachments = [];

    onlyToUpload: boolean = false;
    startDate = moment().format("YYYY-MM-DD");

    get endDate() {
        let endDate = moment();
        let startDate = moment(this.startDate);
        // console.log("tto", endDate.diff(startDate,"days"))
        if (endDate.diff(startDate, "days") > 30) {
            endDate = startDate.add(30, "days");
        }
        return endDate.format("YYYY-MM-DD");
    }

    get toUploadCount() {
        let toUploadCount = 0;
        for (const attachment of this.attachments) {
            if (!attachment.document) {
                toUploadCount++;
            }
        }
        return toUploadCount;
    }

    get report() {
        if (this.attachments.length) {
            let report = this.attachments.length + " document(s)";
            report += " dont " + this.toUploadCount + " à importer";
            let types = {};
            for (const attachment of this.attachments) {
                if (attachment.document && attachment.document.type) {
                    if (types[attachment.document.type]) {
                        types[attachment.document.type]++;
                    } else {
                        types[attachment.document.type] = 1;
                    }
                }
            }
            for (const type in types) {
                if (type && types[type]) {
                    report +=
                        " et " +
                        types[type] +
                        " " +
                        this.$t("document.type." + type) +
                        (type != "UNKNOWN" ? "(s)" : "");
                }
            }
            return report;
        }
        return "";
    }

    get selectedCompany() {
        return this.$store.state.selected_company;
    }

    @Watch("startDate")
    onDateChanged() {
        this.attachments = [];
    }

    formatDate(date) {
        if (!date) return null;
        return moment(date).format("DD MMM YYYY");
    }

    get isDateValid() {
        if (!this.startDate) return false;
        if (!moment(this.startDate).isValid()) {
            return false;
        }
        return true;
    }

    get filteredAttachments() {
        if (this.onlyToUpload) {
            return this.attachments.filter((d) => !d.document);
        }
        return this.attachments;
    }

    getAttachmentType(item) {
        return item.document ? " - " + this.$t("document.type." + item.document.type) : "";
    }

    getAttachmentStatus(item) {
        return item.document ? "Importé" : "A importer";
    }

    getSenderOrReceiver(itemToOrFrom) {
        if (itemToOrFrom?.address) {
            return itemToOrFrom.address;
        }
        return itemToOrFrom.match(new RegExp(findEmailIntoString))[0] || "";
    }

    get actionTitle() {
        if (this.attachments.length) {
            let count = 0;
            for (const attachement of this.attachments) {
                if (attachement.selected) {
                    count++;
                }
            }
            if (count) {
                return count === 1 ? "Importer le document" : "Importer les documents";
            }
        }
        return "Rechercher";
    }

    get actionDisabled() {
        if (!this.isDateValid) {
            return true;
        }
        if (this.attachments.length) {
            let count = 0;
            for (const attachement of this.attachments) {
                if (attachement.selected) {
                    count++;
                }
            }
            if (count) {
                return false;
            }
            return true;
        }
        return false;
    }

    get simulate() {
        return !this.attachments.length;
    }

    async uploadDocumentsFromMail() {
        if (!this.actionDisabled) {
            this.loading = true;
            const result = await this.$apollo.mutate({
                mutation: UPLOAD_MAIL_DOCUMENTS,
                variables: {
                    companyId: this.selectedCompany.id,
                    simulate: this.simulate,
                    lastDate: this.startDate,
                },
            });
            if (result && result.data && result.data.uploadMailDocuments) {
                this.attachments = [...result.data.uploadMailDocuments.result];
                for (const attachement of this.attachments) {
                    attachement.isActive = false;
                    attachement.selected = attachement.document ? false : true;
                }
                if (this.attachments.length && !this.simulate) {
                    this.$emit("attachment-uploaded");
                }
            }
            this.loading = false;
        }
        // if (!simulate) {
        //     this.refetchDocuments()
        // }
    }

    cleanEmail(email) {
        if (email) {
            const result = email.match(new RegExp(/\<(.*)\>/));
            if (result && result.length) {
                return result[1];
            }
        }
        return email;
    }

    getDocumentType(to) {
        const bobbeePattern = /^collect\+(.{1,})@bobbee.co$/;

        const documentTypes = {
            achat: {
                toSplit: false,
                type: "PURCHASE_INVOICE",
                title: "1 Achat",
            },
            achats: {
                toSplit: true,
                type: "PURCHASE_INVOICE",
                title: "Plusieurs achats",
            },
            vente: {
                toSplit: false,
                type: "SALES_INVOICE",
                title: "1 vente",
            },
            ventes: {
                toSplit: true,
                type: "SALES_INVOICE",
                title: "Plusieurs ventes",
            },
            divers: {
                toSplit: false,
                type: "Divers",
            },
        };

        const otoEmails = to.split(",");
        for (const email of otoEmails) {
            const toEmail = this.cleanEmail(email);
            if (toEmail) {
                const result = toEmail.match(bobbeePattern);
                if (result && result.length === 2) {
                    const key = result[1];
                    const values = key.split("+");
                    if (values.length) {
                        let documentTitle = "Inconnu";
                        if (values.length >= 2 && documentTypes[values[0]]) {
                            const documentInfo = documentTypes[values[0]];
                            return documentInfo.title;
                        }
                    }
                }
            }
        }
        return "Inconnu";
    }

    getTo(item) {
        if (item && item.message && item.message.to) {
            const documentType = this.getDocumentType(item.message.to);
            if (documentType) {
                return documentType;
            }
            return item.message.to;
        }
        return "";
    }
}

export default toNative(UploadDocumentsFromMail);
</script>
<style scoped lang="scss"></style>
